import {
  DATA_EXPLORER,
  EntityDataType,
  getAppliedFilters,
  IDataGridProps,
  IProperty,
  IWork,
  prepareWorksForWorkGrid,
  urlWithHost,
  useDataExplorerReportStore,
  useEnvironment,
  useFeatures,
  useHeaderHeightForScrollableContent,
  WorkGrid,
} from '@diana/core';
import { GridApi, GridSortModel } from '@mui/x-data-grid-premium';
import { omit } from 'lodash';
import { forwardRef, useCallback, useMemo } from 'react';
import { routeUrls } from '../../app/Routing/RouteUrls';

interface PerformancePageProps {
  works: IWork[];
  isLoading: boolean;
  forceEmptySelection: boolean;
  supportedProperties: IProperty[];
  fetchNextPage: () => void;
  onSortModelChange: (model: GridSortModel) => void;
  aggregateColumnOperation: IDataGridProps['aggregateColumnOperation'];
}

export const DataExplorerReportTable = forwardRef<GridApi, PerformancePageProps>((props, ref) => {
  const {
    works,
    isLoading,
    forceEmptySelection,
    supportedProperties,
    fetchNextPage,
    onSortModelChange,
    aggregateColumnOperation,
  } = props;
  const filtersValues = useDataExplorerReportStore((store) => store.filtersValues);
  const filters = getAppliedFilters({ filtersValues });
  const { disableLastVisitedWork } = useFeatures();
  const headerHeight = useHeaderHeightForScrollableContent();
  const height = `calc(100vh - ${headerHeight}px)`;

  const environment = useEnvironment();
  const createWorkLink = useCallback(
    (work: IWork) => urlWithHost(routeUrls.work.url({ workId: work.id, environment }, {})),
    [environment],
  );

  const preparedWorks = prepareWorksForWorkGrid({
    works,
    properties: supportedProperties,
  });

  const columns = useMemo(() => {
    return supportedProperties.map((e) => ({
      ...e,
      sortable: !!e.valuePath,
      // to avoid default aggregations to automatically trigger in DataExplorer, as it puts a lot of pressure on the server
      cellConfig: omit(e.cellConfig, 'operation'),
    }));
  }, [supportedProperties]);

  return (
    <WorkGrid
      ref={ref}
      isManageColumnsPinned
      storageKey={`${DATA_EXPLORER}-reporting-grid`}
      dataType={EntityDataType.Work}
      rows={preparedWorks}
      disableLastVisited={disableLastVisitedWork}
      forceEmptySelection={forceEmptySelection}
      columns={columns}
      createWorkLink={createWorkLink}
      filters={filters}
      aggregateColumnOperation={aggregateColumnOperation}
      gridProps={{
        height,
        autoHeight: false,
        sortingMode: 'server',
        onSortModelChange,
        loading: isLoading,
        onRowsScrollEnd: () => {
          fetchNextPage();
        },
      }}
    />
  );
});
